import { isNaN } from 'lodash'
import { ClientError } from '../../../../common/utils/Exception'
import { isAfter, isWithinInterval, lastDayOfMonth } from 'date-fns'
import { dataFixStatus } from '../../../../_constants/Code'

/** @function getCurrentFiscalYear */
export const getCurrentFiscalYear = (accPeriod, hasSuffix = false) => {
  const accMonth = extractMonthFromAccPeriod(accPeriod)
  const today = new Date()
  let currentYear = today.getFullYear()
  const currentMonth = today.getMonth() + 1
  const december = 12

  if (accMonth !== december && currentMonth <= accMonth) currentYear--

  return hasSuffix ? `${currentYear}年度` : currentYear
}

/**
 * @function extractMonthFromAccPeriod
 * @param {string} accPeriod
 */
export const extractMonthFromAccPeriod = (accPeriod) => {
  const match = accPeriod.match(/^(\d{1,2})月$/)
  if (!match) throw new ClientError('決算月のフォマットが不正')
  const month = Number(match[1])
  if (isNaN(month)) throw new ClientError('決算月が不正です')

  return month
}

/**
 * @function extractYearMonthFromFiscalPeriod
 * @param {string} fiscalPeriod
 */
export const extractYearMonthFromFiscalPeriod = (fiscalPeriod) => {
  const match = fiscalPeriod.match(/^(\d{4})年(\d{1,2})月期$/)
  if (!match) throw new ClientError('決算期のフォマットが不正です')

  const year = Number(match[1])
  const month = Number(match[2])

  if ([year, month].some(isNaN)) throw new Error('決算期の値が不正です')

  return [year, month]
}

/**
 * @function extractYearFromFiscalYear Yyyy年度
 * @param {string} fiscalYear
 */
export const extractYearFromFiscalYear = (fiscalYear) => {
  const match = fiscalYear.match(/^(\d{4})年度$/)
  if (!match) throw new ClientError('決算年度のフォマットが不正です')

  return Number(match[1])
}

/**
 * @function convertFiscalPeriodToYear
 * @param {string} fiscalPeriod
 * @param {string} accPeriod
 */
export const convertFiscalPeriodToYear = (fiscalPeriod, accPeriod, hasSuffix = true) => {
  let [year] = extractYearMonthFromFiscalPeriod(fiscalPeriod)
  const _accPeriod = extractMonthFromAccPeriod(accPeriod)

  if (_accPeriod !== 12) year--
  return hasSuffix ? `${year}年度` : year
}

/**
 * @function convertFiscalYearToPeriod
 * @param {string} fiscalYear
 * @param {string} month
 * @returns {[string, number, number]} 決算期, 年,決算月
 */
export const convertFiscalYearToPeriod = (fiscalYear, accPeriod) => {
  const match = fiscalYear.match(/^(\d{4})年度$/)
  if (!match) throw new ClientError('決算年度フォマット不正')
  let year = Number(match[1])

  const _accPeriod = extractMonthFromAccPeriod(accPeriod)

  if (_accPeriod !== 12) year++

  return `${year}年${_accPeriod}月期`
}

/**
 * @function getLastDateOfFiscalPeriod
 * @param {string} fiscalPeriod
 * @param {string} accPeriod
 * @returns {Date}
 */
export const getLastDateOfFiscalPeriod = (fiscalPeriod, accPeriod) => {
  const [year] = extractYearMonthFromFiscalPeriod(fiscalPeriod)
  const _accPeriod = extractMonthFromAccPeriod(accPeriod)
  const date = new Date(year, _accPeriod - 1)

  return lastDayOfMonth(date)
}

/**
 * @typedef {object} FiscalYearItem
 * @property {string} fiscalYear
 * @property {string} fiscalYearMOnth
 * @property {number} yearValue
 * @property {number} monthValue
 * @property {string} periodKind
 * @property {string} periodValue
 * @property {string} taskStatus
 * @property {string} dataFixStatus
 */

/**
 * =========
 *
 * @function creatExtraYearList
 * @param {FiscalYearItem[]} yearlist
 * @param {string} accPeriod
 */
export async function createExtraYearList(yearlist, accPeriod) {
  //現在進行年度
  const currentFiscal = getCurrentFiscalYear(accPeriod, false)
  //決算月の値
  const month = extractMonthFromAccPeriod(accPeriod)

  const sortedList = [...yearlist].sort((a, b) => (a.yearValue > b.yearValue ? -1 : 1))
  const sortedMap = new Map(sortedList.map((e) => [e.periodValue, e]))

  //年度表示の上限
  const yearCount = 5
  const minYear = Math.min(...[sortedList?.at(-1)?.yearValue, currentFiscal].filter((x) => x !== undefined))

  /** ==最古================= 現在進行 ===============現在進行+5 == */

  let collect = []
  for (let fiscalYear = currentFiscal + yearCount; fiscalYear >= minYear; fiscalYear--) {
    const fiscalYearValue = month === 12 ? fiscalYear : fiscalYear + 1
    const periodValue = `${fiscalYearValue}年${accPeriod}期`

    collect.push({
      fiscalYear: `${fiscalYear}年度`,
      lastDateOfFiscalPeriod: getLastDateOfFiscalPeriod(periodValue, accPeriod),
      yearValue: fiscalYear,
      monthValue: month,
      periodValue: periodValue,
      dataFixStatus: sortedMap.get(periodValue)?.dataFixStatus ?? dataFixStatus.pending.value
    })
  }
  return collect
}

/**
 * @function filterGroupCompanyPeriod
 * @param {GroupCompanyInfoResponse[]} groupCompanyList
 * @param {string} fiscalPeriod -- yyyy年MM月期
 * @param {string} accPeriod --決算期
 */
export const filterGroupCompanyPerPeriod = (groupCompanyList, fiscalPeriod, accPeriod) => {
  const fiscalYearMonth = getLastDateOfFiscalPeriod(fiscalPeriod, accPeriod)

  return groupCompanyList.filter((e) => {
    if (e.validStartDate === null && e.validEndDate === null) return true
    else if (e.validStartDate !== null && e.validEndDate === null) return isAfter(fiscalYearMonth, e.validStartDate)
    else if (e.validStartDate !== null && e.validEndDate !== null)
      return isWithinInterval(fiscalYearMonth, {
        start: e.validStartDate,
        end: e.validEndDate
      })
    else false
  })
}
